import React from 'react';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useBookingCopyTranslation as useTranslation } from '../../i18n/bookingCopy';

export interface TutorBookingRequestsPageShellProps {
  title: string;
  subtitle?: string;
  pendingActionCount?: number;
  loading: boolean;
  hasLoadedOnce?: boolean;
  error: string | null;
  onRetry?: () => void;
  onDismissError?: () => void;
  emptyTitle: string;
  emptyDescription: string;
  isEmpty: boolean;
  children: React.ReactNode;
}

const TutorBookingRequestsPageShell: React.FC<TutorBookingRequestsPageShellProps> = ({
  title,
  subtitle,
  pendingActionCount = 0,
  loading,
  hasLoadedOnce = true,
  error,
  onRetry,
  onDismissError,
  emptyTitle,
  emptyDescription,
  isEmpty,
  children,
}) => {
  const { t } = useTranslation();
  const showInitialLoading = loading && !hasLoadedOnce;

  return (
    <Box sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2 }, pb: { xs: 8, sm: 10 } }}>
      <Card elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Box>
            <Typography variant="h5" component="h1" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 560 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {pendingActionCount > 0 ? (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={t('tutorBookingRequests.pendingActionCount', '{{count}} need action', {
                count: pendingActionCount,
              })}
              sx={{ fontWeight: 600, flexShrink: 0 }}
            />
          ) : null}
        </Stack>
        <Divider sx={{ mb: { xs: 2, sm: 3 } }} />

        {error ? (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={onDismissError}
            action={
              onRetry ? (
                <Button color="inherit" size="small" onClick={onRetry}>
                  {t('common.retry', 'Retry')}
                </Button>
              ) : undefined
            }
          >
            {error}
          </Alert>
        ) : null}

        {showInitialLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ opacity: loading ? 0.65 : 1, transition: 'opacity 0.2s ease' }}>
            {children}
            {isEmpty ? (
              <Card
                elevation={0}
                sx={{
                  p: { xs: 3, sm: 5 },
                  mt: 2,
                  textAlign: 'center',
                  bgcolor: 'action.hover',
                  borderRadius: 2,
                  border: '1px dashed',
                  borderColor: 'divider',
                }}
              >
                <EventAvailableOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
                <Typography variant="h6" gutterBottom={emptyDescription.trim() !== emptyTitle.trim()}>
                  {emptyTitle}
                </Typography>
                {emptyDescription.trim() !== emptyTitle.trim() ? (
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto' }}>
                    {emptyDescription}
                  </Typography>
                ) : null}
              </Card>
            ) : null}
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default TutorBookingRequestsPageShell;
